import {priority} from '@priority-software/priority-proto-api/lib';
import {isHour12Format, parseHours, parseMinutes} from './time';
import {getLang, getLocale} from '../../../Localization';
import {Field} from '../../../stores/Form/Field';
import {unShiftUTCDateTimeToCurrentTimeZone} from '@priority-software/priority-utils/lib/date';

export function getLocaleForDateFormat(lang: string): string {
    return lang === '3' ? 'en' : 'en-GB';
}

export function isDate(type: priority.netitems.form.ColumnType): boolean {
    return (
        type === priority.netitems.form.ColumnType.Date8 ||
        type === priority.netitems.form.ColumnType.Date10 ||
        type === priority.netitems.form.ColumnType.Date14
    );
}

export function isDateTime(type: priority.netitems.form.ColumnType) {
    return type === priority.netitems.form.ColumnType.Date14;
}

export function getFormatByColumnType(
    type: priority.netitems.form.ColumnType,
    lang: string
): Intl.DateTimeFormatOptions {
    if (type === priority.netitems.form.ColumnType.Date8) {
        return {year: '2-digit', month: '2-digit', day: '2-digit'};
    }
    if (type === priority.netitems.form.ColumnType.Date10) {
        return {year: 'numeric', month: '2-digit', day: '2-digit'};
    }
    if (type === priority.netitems.form.ColumnType.Date14) {
        return {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: isHour12Format(lang)
        };
    }
    if (type === priority.netitems.form.ColumnType.Time5) {
        return {
            hour: '2-digit',
            minute: '2-digit',
            hour12: isHour12Format(lang)
        };
    }
    return null;
}

export function getStringFormatByColumnType(
    type: priority.netitems.form.ColumnType,
    lang: string
): string {
    if (type === priority.netitems.form.ColumnType.Date8) {
        return lang === '3' ? 'MM/DD/YY' : 'DD/MM/YY';
    }
    if (type === priority.netitems.form.ColumnType.Date10) {
        return lang === '3' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
    }
    if (type === priority.netitems.form.ColumnType.Date14) {
        return lang === '3' ? 'MM/DD/YY hh:mm a' : 'DD/MM/YY HH:mm';
    }
    if (type === priority.netitems.form.ColumnType.Time5) {
        return lang === '3' ? 'hh:mm a' : 'HH:mm';
    }
    return null;
}

const appLang = getLang();
export const intlInstancesByType = {
    [priority.netitems.form.ColumnType.Date8]: new Intl.DateTimeFormat(
        getLocaleForDateFormat(appLang),
        getFormatByColumnType(priority.netitems.form.ColumnType.Date8, appLang)
    ),
    [priority.netitems.form.ColumnType.Date10]: new Intl.DateTimeFormat(
        getLocaleForDateFormat(appLang),
        getFormatByColumnType(priority.netitems.form.ColumnType.Date10, appLang)
    ),
    [priority.netitems.form.ColumnType.Date14]: new Intl.DateTimeFormat(
        getLocaleForDateFormat(appLang),
        getFormatByColumnType(priority.netitems.form.ColumnType.Date14, appLang)
    ),
    [priority.netitems.form.ColumnType.Time5]: new Intl.DateTimeFormat(
        getLocaleForDateFormat(appLang),
        getFormatByColumnType(priority.netitems.form.ColumnType.Time5, appLang)
    )
};

export const localizedWeekDays: string[] = buildWeekdaysArray();

export function buildWeekdaysArray() {
    const localizedWeekDays = new Array(7).fill(null);
    const curDate = new Date();
    const weekdayFormat = getLang() === '1' ? 'narrow' : 'short';
    for (let i = 0; i < 7; i++, curDate.setDate(curDate.getDate() + 1)) {
        const datInt = curDate.getDay();
        const localizedWeekDay = curDate.toLocaleString(getLocale(), {weekday: weekdayFormat});
        localizedWeekDays[datInt] =
            getLang() === '1' ? localizedWeekDay.replace('׳', '') : localizedWeekDay;
    }
    return localizedWeekDays;
}

export function formatDate(date: Date, type: priority.netitems.form.ColumnType): string {
    const dateStr: string = intlInstancesByType[type]?.format(date) || '';
    if (type === priority.netitems.form.ColumnType.Date14) {
        return dateStr.replace(/, /g, ' ');
    }
    return dateStr;
}

export function isAutoCompleteApplicable(
    value: string,
    type: priority.netitems.form.ColumnType
): boolean {
    if (type === priority.netitems.form.ColumnType.Time5) {
        return !value.length;
    }
    if (!value.length || value.length === 3 || value.length === 6) {
        return true;
    }
    if (type === priority.netitems.form.ColumnType.Date14) {
        return value.length === 9 || value.length === 12 || value.length === 15;
    }
    return false;
}

export function autocompleteFromCurrentDate(
    value: string,
    type: priority.netitems.form.ColumnType
) {
    const curDateTime = formatDate(new Date(), type);
    return `${value}${curDateTime.slice(value.length, curDateTime.length)}`;
}

export function isMaxLengthReached(
    value: string,
    type: priority.netitems.form.ColumnType,
    lang: string
): boolean {
    if (type === priority.netitems.form.ColumnType.Date8) {
        return value?.length === 8;
    }
    if (type === priority.netitems.form.ColumnType.Date10) {
        return value?.length === 10;
    }
    if (type === priority.netitems.form.ColumnType.Date14) {
        return lang === '3' ? value?.length === 17 : value?.length === 14;
    }
    if (type === priority.netitems.form.ColumnType.Time5) {
        return lang === '3' ? value?.length === 8 : value?.length === 5;
    }
    return false;
}

export function formatStringToDate(value: string, isDateTime: boolean, lang: string): Date | null {
    let dateString = value;
    let year: number;
    let month: number;
    let day: number;
    let hours: number | undefined;
    let minutes: number | undefined;

    if (isDateTime) {
        const dateTimeParts = value.split(' ');
        dateString = dateTimeParts[0];
        const time = dateTimeParts[1];
        const period = dateTimeParts[2];
        hours = parseHours(time, period);
        minutes = parseMinutes(time);
    }

    const parts = dateString.split('/');
    if (parts.length !== 3) return null; // Invalid date format. Early exit from function

    year = parseInt(parts[2], 10);

    if (parts[2]?.length === 2) {
        let centuryPart = '20';
        if (parseInt(parts[2], 10) >= 88) {
            centuryPart = '19';
        }
        year = Number(centuryPart + parts[2]);
    }

    if (lang === '3') {
        month = parseInt(parts[0], 10);
        day = parseInt(parts[1], 10);
    } else {
        month = parseInt(parts[1], 10);
        day = parseInt(parts[0], 10);
    }
    // Validation for month value being greater than 12
    if (month > 12) {
        // Switch day and month values
        const tempMonth = month;
        month = day;
        day = tempMonth;
    }

    const date = new Date(year, month - 1, day, hours || 0, minutes || 0);

    if (date.toString() === 'Invalid Date') {
        return null;
    }
    return date;
}

export type APIDateFormat = {
    year?: number;
    month?: number;
    day?: number;
    hours?: number;
    minutes?: number;
};

export const decodeStringDateByType = (
    dateString: string,
    type: priority.netitems.form.ColumnType
) => {
    if (!dateString) return null;

    const date = formatStringToDate(dateString, isDateTime(type), getLang());
    // Function formatStringToDate already has condition for check date.toString() === 'Invalid Date' line:202
    if (date === null) return null;

    const dateData: APIDateFormat = {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
    };

    if (type === priority.netitems.form.ColumnType.Date14) {
        unShiftUTCDateTimeToCurrentTimeZone(date);
        dateData.hours = date.getHours();
        dateData.minutes = date.getMinutes();
    }

    return dateData;
};

export const formatTime = (time: string, is12Hour: boolean) => {
    const tempDate = new Date(`01/01/23 ${time}`);
    return new Intl.DateTimeFormat('default', {
        hour: '2-digit',
        hour12: is12Hour,
        minute: '2-digit'
    })
        .format(tempDate)
        .toUpperCase();
};

export const getDateAsMMDDYYYY = (date: string, isDateTime: boolean, lang: string) => {
    if (!date) {
        return date;
    }
    const wildcard = /\*|\?/g;
    if (wildcard.test(date)) {
        if (lang !== '3') {
            const parts = date.split('/');
            const day = parts[0];
            const month = parts[1];
            parts[0] = month;
            parts[1] = day;
            return parts.join('/');
        }
        return date;
    }
    const dateType = new Date(date);
    if (dateType.toString() === 'Invalid Date') {
        return date;
    }
    return new Intl.DateTimeFormat(
        'en-US',
        isDateTime
            ? {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  hour12: false,
                  minute: '2-digit'
              }
            : {}
    ).format(dateType);
};

export const getDateStringlocalFormat = (date: string, lang: string) => {
    if (!date) {
        return date;
    }

    if (lang !== '3') {
        const parts = date.split('/');
        const day = parts[1];
        const month = parts[0];
        parts[0] = day;
        parts[1] = month;
        return parts.join('/');
    }
    return date;
};

export const isIsoDate = str => {
    if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false;
    const d = new Date(str);
    return d instanceof Date && !Number.isNaN(d) && d.toISOString() === str; // valid date
};

export const getDateObjectFromField = (field: Field): Date => {
    const date = field?.Value?.Date;
    if (date) {
        const {year, month, day} = date;
        const dateInRecord = new Date(year, month - 1, day);
        return dateInRecord;
    }
    return null;
};

export const isDateByStringStructure = (string: string): boolean => {
    const isDateRegex = new RegExp(/(\d{2}|\?\?)[-/](\d{2}|\?\?)[-/](\d{2}|\?\?)/);
    return isDateRegex.test(string);
};
